"use client";

import React, { useCallback } from "react";
import { Building2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRouter } from "next/navigation";
import { CompanyContacts } from "@/features/companies/components/CompanyContacts";
import { useModalControls } from "@/lib/modals";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";

export const RecurringTransactionCompanyDisplay = React.memo(
  ({ item }: TabContentProps<RecurringTransaction>) => {
    const router = useRouter();
    const { openModal: openCompanyModal } = useModalControls("companies-detail");

    const companyId = item.company_id;
    const companyName = item.company_name ?? (item.company as any)?.name;

    const handleViewFullCompany = useCallback(() => {
      if (companyId) {
        openCompanyModal({ companiesId: companyId });
      } else if (companyName) {
        router.push(`/companies?search=${encodeURIComponent(companyName)}`);
      }
    }, [router, companyId, companyName, openCompanyModal]);

    if (!companyName && !companyId) {
      return (
        <Card>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No company associated with this recurring transaction</p>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="w-full space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0" />
            <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">Company Details</h3>
          </div>
          <Button variant="outline" size="sm" onClick={handleViewFullCompany} className="gap-2 h-11 sm:h-8 shrink-0">
            <ExternalLink className="w-4 h-4" />
            View Full Company
          </Button>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Company information for this recurring transaction
        </div>

        {companyId && <CompanyContacts />}
      </div>
    );
  }
);

RecurringTransactionCompanyDisplay.displayName = "RecurringTransactionCompanyDisplay";
